import React, {useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  createTheme,
  FormControlLabel,
  Stack,
  TextField,
  ThemeProvider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import {BaseFullPage} from '../base';
import {useParamState} from '../../hooks';
import {TranslateKey, useTranslate} from '../../hooks/useTranslate';
import {AdditionalDocuments} from './AdditionalDocuments';
import {MainDocumentBox} from './MainDocumentBox';
import {SigningResponseView} from './SigningResponseView';
import {DEFAULT_SIGNER_ID, SignersEditor} from './SignersEditor';
import {
  DEFAULT_MAIN_AGREEMENT_TYPE,
  DEFAULT_MAIN_PDF,
  useSigningDemo,
  CreateSigningDemoResponse,
  SigningDemoFormParams,
} from '../../hooks/useSigningDemo';

type AlertSeverity = 'success' | 'warning' | 'error';

const statusAlert: Record<
  string,
  {severity: AlertSeverity; key: TranslateKey}
> = {
  SUCCESSFUL: {severity: 'success', key: 'demo.signing.status.successful'},
  CANCELLED_VIA_API: {
    severity: 'warning',
    key: 'demo.signing.status.canceled',
  },
  REJECTED: {severity: 'warning', key: 'demo.signing.status.rejected'},
  EXPIRED: {severity: 'warning', key: 'demo.signing.status.expired'},
  UNKNOWN: {severity: 'error', key: 'demo.signing.status.failed'},
  SIGNER_MISMATCH: {severity: 'error', key: 'demo.signing.status.failed'},
};

const defaultValues: SigningDemoFormParams = {
  governmentId: '198004042399',
  fullName: 'Peter Snook',
  displayText:
    'I want to sign my loan agreement.\nLoan amount: 50 000 SEK\nInterest rate: 9.95%\nRepayment period: 36 months\nMonthly payment: 1650 SEK\n\n\nStay safe: Resurs Bank will never call, text or email asking you to log in or sign with BankID. If someone does, cancel and contact us on 0771-11 22 33.',
  intentDisplayText:
    'I hereby understand that I am signing an agreement with Resurs Bank.',
  mainDocumentPdf: DEFAULT_MAIN_PDF,
  additionalDocuments: [],
  signers: [
    {
      id: DEFAULT_SIGNER_ID,
      governmentId: '198004042399',
      fullName: 'Anders Testsson',
      firstName: 'Anders',
      lastName: 'Testsson',
    },
  ],
  costBearer: 'Jula',
  legalGovernmentId: '516401-0208',
  endUserIp: '127.0.0.1',
  signingType: 'SIMPLE_SIGNING',
  documentViewAuthRequired: true,
};

export const SigningDemo = () => {
  const t = useTranslate();
  const [{status}] = useParamState<{status?: string}>();
  const [form, setForm] = useState<SigningDemoFormParams>(defaultValues);
  const signingDemo = useSigningDemo();
  const [error, setError] = useState<string>();
  const [signingResponse, setSigningResponse] =
    useState<CreateSigningDemoResponse>();
  const parentTheme = useTheme();
  const isImmediate = form.signingType === 'IMMEDIATE_SIGNING';
  const documentBased = form.signingType !== 'SIMPLE_SIGNING';

  const formTheme = React.useMemo(
    () =>
      createTheme(parentTheme, {
        components: {
          MuiTextField: {
            defaultProps: {
              variant: 'outlined',
              autoComplete: 'off',
              InputProps: {},
            },
            styleOverrides: {root: {}},
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {},
              notchedOutline: {borderWidth: 1},
              input: {padding: '14px', margin: 0, fontWeight: 400},
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {margin: 0},
              formControl: {margin: 0},
              shrink: {margin: 0},
            },
          },
        },
      }),
    [parentTheme],
  );

  const handleChange = (field: keyof SigningDemoFormParams, value: string) => {
    setForm((prev) => ({...prev, [field]: value}));
  };

  const handleCheckboxChange = (
    field: keyof SigningDemoFormParams,
    checked: boolean,
  ) => {
    setForm((prev) => ({...prev, [field]: checked}));
  };

  const handleSubmit = async () => {
    setError(undefined);
    setSigningResponse(undefined);
    try {
      const response = await signingDemo.mutateAsync(form);
      setSigningResponse(response);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('demo.signing.error.unknown'),
      );
    }
  };

  return (
    <BaseFullPage id={'signing_demo'} containerMaxWidth={false} noTopBar>
      <Stack spacing={3} sx={{py: 2}}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            columnGap: 2,
          }}>
          <Typography variant="h4" component="h1">
            {t('demo.signing.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('demo.signing.subtitle')}
          </Typography>
        </Box>

        {status && statusAlert[status] && (
          <Alert severity={statusAlert[status].severity}>
            {t(statusAlert[status].key)}
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 2,
          }}>
          <ToggleButtonGroup
            exclusive
            value={form.signingType}
            onChange={(_, value: SigningDemoFormParams['signingType'] | null) =>
              value && setForm((prev) => ({...prev, signingType: value}))
            }>
            <ToggleButton value="SIMPLE_SIGNING">
              {t('demo.signing.type.simple')}
            </ToggleButton>
            <ToggleButton value="NORMAL_SIGNING">
              {t('demo.signing.type.normal')}
            </ToggleButton>
            <ToggleButton value="IMMEDIATE_SIGNING">
              {t('demo.signing.type.immediate')}
            </ToggleButton>
          </ToggleButtonGroup>
          {form.signingType === 'NORMAL_SIGNING' && (
            <Alert severity="info" sx={{flex: 1, minWidth: 300}}>
              {t('demo.signing.info.normal')}
            </Alert>
          )}
          {isImmediate && (
            <Alert severity="info" sx={{flex: 1, minWidth: 300}}>
              {t('demo.signing.info.immediate')}
            </Alert>
          )}
        </Box>

        {form.signingType === 'NORMAL_SIGNING' && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.documentViewAuthRequired}
                  onChange={(e) =>
                    handleCheckboxChange(
                      'documentViewAuthRequired',
                      e.target.checked,
                    )
                  }
                />
              }
              label={t('demo.signing.documentViewAuthRequired')}
            />
          </>
        )}

        <ThemeProvider theme={formTheme}>
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'},
            }}>
            {form.signingType === 'SIMPLE_SIGNING' && (
              <>
                <TextField
                  label={t('demo.signing.field.governmentId')}
                  value={form.governmentId}
                  onChange={(e) => handleChange('governmentId', e.target.value)}
                  fullWidth
                />
                <TextField
                  label={t('demo.signing.field.fullName')}
                  value={form.fullName}
                  onChange={(e) => handleChange('fullName', e.target.value)}
                  fullWidth
                />
              </>
            )}
            {form.signingType === 'SIMPLE_SIGNING' ? (
              <TextField
                key="display-text"
                label={t('demo.signing.field.displayText')}
                value={form.displayText}
                onChange={(e) => handleChange('displayText', e.target.value)}
                multiline
                minRows={3}
                fullWidth
                sx={{gridColumn: {md: '1 / -1'}}}
              />
            ) : (
              <React.Fragment key="normal-signing-fields">
                <Stack spacing={3}>
                  <TextField
                    label={t('demo.signing.field.intentDisplayText')}
                    helperText={t('demo.signing.field.intentDisplayTextHelper')}
                    value={form.intentDisplayText}
                    onChange={(e) =>
                      handleChange('intentDisplayText', e.target.value)
                    }
                    multiline
                    minRows={2}
                    fullWidth
                  />
                  <TextField
                    label={t('demo.signing.field.legalGovernmentId')}
                    helperText={t('demo.signing.field.legalGovernmentIdHelper')}
                    value={form.legalGovernmentId}
                    onChange={(e) =>
                      handleChange('legalGovernmentId', e.target.value)
                    }
                    fullWidth
                  />
                </Stack>
                <MainDocumentBox
                  file={form.mainDocumentPdf ?? DEFAULT_MAIN_PDF}
                  agreementType={
                    form.mainDocumentAgreementType ??
                    DEFAULT_MAIN_AGREEMENT_TYPE
                  }
                  onFileChange={(mainDocumentPdf) =>
                    setForm((prev) => ({...prev, mainDocumentPdf}))
                  }
                  onAgreementTypeChange={(mainDocumentAgreementType) =>
                    setForm((prev) => ({...prev, mainDocumentAgreementType}))
                  }
                />
              </React.Fragment>
            )}
            {documentBased && (
              <Box sx={{gridColumn: {md: '1 / -1'}}}>
                <SignersEditor
                  single={isImmediate}
                  signers={form.signers ?? []}
                  onChange={(signers) =>
                    setForm((prev) => ({...prev, signers}))
                  }
                />
              </Box>
            )}
            {documentBased && (
              <Box sx={{gridColumn: {md: '1 / -1'}}}>
                <AdditionalDocuments
                  documents={form.additionalDocuments ?? []}
                  onChange={(update) =>
                    setForm((prev) => ({
                      ...prev,
                      additionalDocuments: update(
                        prev.additionalDocuments ?? [],
                      ),
                    }))
                  }
                />
              </Box>
            )}
          </Box>
        </ThemeProvider>

        {error && <Alert severity="error">{error}</Alert>}

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={signingDemo.isPending || !!signingResponse}
          sx={{alignSelf: 'flex-end'}}>
          {signingDemo.isPending ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t('demo.signing.start')
          )}
        </Button>

        {signingResponse && <SigningResponseView response={signingResponse} />}
      </Stack>
    </BaseFullPage>
  );
};
